/**
 * Проверка программы Робота на одной или нескольких картах.
 * Ученик видит display-карту из workspace_config; проверка гоняет check_maps.
 */

import { runPython } from '../components/PyodideRunner';
import { OgeRobotLangError, prepareRobotSource } from './ogeRobotLang';

function cellKey(cell) {
  if (Array.isArray(cell) && cell.length >= 2) return `${Number(cell[0])},${Number(cell[1])}`;
  if (cell && typeof cell === 'object') {
    return `${Number(cell.r ?? cell.row ?? 0)},${Number(cell.c ?? cell.col ?? 0)}`;
  }
  return String(cell);
}

function setFromCells(cells) {
  const s = new Set();
  for (const c of cells || []) s.add(cellKey(c));
  return s;
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

/**
 * Карты для проверки: check_maps или одна из top-level config.
 * @param {object} workspaceConfig
 * @returns {object[]}
 */
export function resolveRobotCheckMaps(workspaceConfig) {
  const cfg = workspaceConfig && typeof workspaceConfig === 'object' ? workspaceConfig : {};
  const maps = Array.isArray(cfg.check_maps) ? cfg.check_maps.filter((m) => m && typeof m === 'object') : [];
  if (maps.length) {
    return maps.map((m, i) => ({
      rows: m.rows ?? cfg.rows ?? 8,
      cols: m.cols ?? cfg.cols ?? 8,
      start: m.start ?? cfg.start ?? [0, 0],
      walls: m.walls ?? [],
      painted: m.painted ?? [],
      goal_painted: m.goal_painted ?? m.goal ?? [],
      label: m.label || `карта ${i + 1}`,
    }));
  }
  if (Array.isArray(cfg.goal_painted) || Array.isArray(cfg.goal)) {
    return [{
      rows: cfg.rows ?? 8,
      cols: cfg.cols ?? 8,
      start: cfg.start ?? [0, 0],
      walls: cfg.walls ?? [],
      painted: cfg.painted ?? [],
      goal_painted: cfg.goal_painted ?? cfg.goal ?? [],
      label: 'карта 1',
    }];
  }
  return [];
}

export function robotConfigHasCheck(workspaceConfig) {
  return resolveRobotCheckMaps(workspaceConfig).length > 0;
}

/**
 * @param {string} sourceCode — текст ученика (Python или ОГЭ)
 * @param {object} workspaceConfig
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function checkRobotProgram(sourceCode, workspaceConfig, options = {}) {
  let pythonCode;
  try {
    pythonCode = prepareRobotSource(sourceCode, workspaceConfig);
  } catch (e) {
    if (e instanceof OgeRobotLangError) {
      return {
        ok: false,
        message: e.message,
        results: [],
      };
    }
    throw e;
  }

  const maps = resolveRobotCheckMaps(workspaceConfig);
  if (!maps.length) {
    return {
      ok: false,
      message: 'У задачи нет карт для проверки (goal_painted / check_maps).',
      results: [],
    };
  }

  const results = [];
  for (const map of maps) {
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const canvasConfig = {
      mode: 'robot',
      silent: true,
      rows: map.rows,
      cols: map.cols,
      start: map.start,
      walls: map.walls,
      painted: map.painted,
    };
    const run = await runPython(pythonCode, '', {}, {
      signal: options.signal,
      interactiveStdin: false,
      echoInput: false,
      canvasConfig,
      onCanvas: () => {},
    });
    const state = run.robotState && typeof run.robotState === 'object'
      ? run.robotState
      : { alive: false, painted: [], message: run.error || '' };
    const destroyed = state.alive === false
      || /Робот разрушен|RobotDestroyed/i.test(String(run.error || ''));
    const alive = !destroyed;
    const runtimeError = !run.success && !destroyed;
    const painted = setFromCells(state.painted);
    const goal = setFromCells(map.goal_painted);
    const paintOk = setsEqual(painted, goal);
    const mapOk = !runtimeError && alive && paintOk;
    let detail = '';
    if (runtimeError) {
      detail = run.error || 'ошибка выполнения';
    } else if (!alive) {
      detail = state.message || run.error || 'Робот разрушен';
    } else if (!paintOk) {
      const missing = [...goal].filter((k) => !painted.has(k));
      const extra = [...painted].filter((k) => !goal.has(k));
      const parts = [];
      if (missing.length) parts.push(`не закрашены: ${missing.join('; ')}`);
      if (extra.length) parts.push(`лишние: ${extra.join('; ')}`);
      detail = parts.join('; ') || 'закраска не совпала';
    }
    results.push({
      ok: mapOk,
      label: map.label,
      alive,
      painted: [...painted].sort(),
      goal: [...goal].sort(),
      detail,
      error: run.success ? '' : (run.error || ''),
    });
  }

  const ok = results.every((r) => r.ok);
  const failed = results.filter((r) => !r.ok);
  let message;
  if (ok) {
    message = results.length === 1
      ? 'Верно: закраска совпала, робот жив.'
      : `Верно на всех картах (${results.length}).`;
  } else if (failed.length === 1) {
    message = `Неверно (${failed[0].label}): ${failed[0].detail || failed[0].error || 'ошибка'}`;
  } else {
    message = `Неверно на ${failed.length} из ${results.length} карт. Первая ошибка — ${failed[0].label}: ${failed[0].detail || failed[0].error}`;
  }

  return { ok, message, results };
}
